const DEFAULT_STYLE = 'mapbox/streets-v12';
const DEFAULT_ATTRIBUTION =
  'Map data © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>';

function normalizeStyle(style) {
  if (!style) return DEFAULT_STYLE;
  const trimmed = style.trim();
  if (!trimmed) return DEFAULT_STYLE;
  if (trimmed.startsWith('mapbox://styles/')) {
    return trimmed.replace('mapbox://styles/', '');
  }
  if (trimmed.startsWith('styles/')) {
    return trimmed.replace('styles/', '');
  }
  return trimmed;
}

export function getMapboxTileLayer(styleOverride) {
  const token = (process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '').trim();
  if (!token) return null;
  const style = normalizeStyle(styleOverride || process.env.REACT_APP_MAPBOX_STYLE_URL);
  return {
    url: `https://api.mapbox.com/styles/v1/${style}/tiles/256/{z}/{x}/{y}?access_token=${token}`,
    attribution: DEFAULT_ATTRIBUTION,
  };
}

const _geocodeCache = new Map();

export async function reverseGeocodeMapbox(lat, lon) {
  const token = (process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '').trim();
  if (!token || lat == null || lon == null) return null;
  const numLat = parseFloat(lat);
  const numLon = parseFloat(lon);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLon)) return null;
  const key = `${numLat.toFixed(6)},${numLon.toFixed(6)}`;
  if (_geocodeCache.has(key)) return _geocodeCache.get(key);
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${numLon},${numLat}.json?types=address,poi&limit=1&access_token=${token}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    const result = data.features?.[0]?.place_name || null;
    _geocodeCache.set(key, result);
    return result;
  } catch {
    return null;
  }
}

export async function searchMapboxPlaces(query, { proximity, limit = 5 } = {}) {
  const token = (process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '').trim();
  const normalizedQuery = String(query || '').trim();
  if (!token || normalizedQuery.length < 2) return [];
  const params = new URLSearchParams({
    access_token: token,
    limit: String(Math.max(1, Math.min(10, limit))),
    autocomplete: 'true',
    country: 'US',
    types: 'poi,address,place,locality,neighborhood,district',
  });
  if (Array.isArray(proximity) && proximity.length === 2) {
    params.set('proximity', `${proximity[0]},${proximity[1]}`);
  }
  const response = await fetch(
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(normalizedQuery)}.json?${params}`,
  );
  if (!response.ok) throw new Error('Location search is unavailable right now.');
  const data = await response.json();
  return (data.features || [])
    .filter((feature) => Array.isArray(feature.center) && feature.center.length >= 2)
    .map((feature) => ({
      id: feature.id,
      label: feature.place_name || feature.text,
      centerLon: Number(feature.center[0]),
      centerLat: Number(feature.center[1]),
    }));
}

export { DEFAULT_ATTRIBUTION };
